import Accordion from "@/common/components/Accordion"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import Checkbox from "@/common/components/Checkbox"
import Spinner from "@/common/components/Spinner"
import TextField from "@/common/components/TextField"
import { formatCurrency, formatPercentage } from "@/common/utils/formatters"
import { FormTransformers } from "@/common/utils/formTransformers"
import { useUpdateFeeTemplate } from "@/features/feeTemplates/hooks/useUpdateFeeTemplate"
import { feeTemplateDefaultValues } from "@/features/feeTemplates/utils/feeTemplateDefaultValues"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { ChevronsRight } from "lucide-react"
import { useEffect } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import { strategy as applicableEntityStrategy } from "../../constants/applicableEntity.constant"
import { strategy as calculateAsStrategy } from "../../constants/calculateAs.constant"
import { strategy as feeTemplateKindStrategy } from "../../constants/feeTemplateKind.constant"
import { strategy as paymentModalityStrategy } from "../../constants/paymentModality.constant"
import useFeeTemplate from "../../hooks/useFeeTemplate"
import { FeeTemplateUpdateValidationSchema, updateSchema } from "../../schemas/create.schema"
import GoBackButton from "@/common/components/GoBackButton";
import { applicableEstablishmentCommercialKind } from "../../constants/applicableEstablishmentCommercialKind.constant"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import useUserPermissions from "@/common/hooks/useUserPermissions"
import { useGoBack } from "@/common/hooks/useGoBack"

const Update = () => {
  const { id } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const { data: initialFeeTemplate, isLoading } = useFeeTemplate(id || '')

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateFeeTemplatePermission = !isUserPermissionsPending ? hasUserPermission('Admin::Billing::FeeTemplates::Update') : true

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<FeeTemplateUpdateValidationSchema>({
    resolver: zodResolver(updateSchema),
    defaultValues: feeTemplateDefaultValues(),
  })

  useEffect(() => {
    if (!isLoading) reset(feeTemplateDefaultValues(initialFeeTemplate))
  }, [isLoading])

  const { mutate: updateFeeTemplate, isPending: updateFeeTemplateIsPending } = useUpdateFeeTemplate({
    feeTemplateId: id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeTemplates'] })
      goBack({ fallback: `/fee-templates/${id}` })
    },
    onError: FormTransformers.errorsTransformer<FeeTemplateUpdateValidationSchema>({ setError }),
  })

  const onSubmit: SubmitHandler<FeeTemplateUpdateValidationSchema> = (data) => {
    updateFeeTemplate(data)
  }

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateFeeTemplatePermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/fee-templates/${id}`} />
            <p className="text-white-400 font-normal">Tarifas</p>
            <ChevronsRight color="#ffffffb3" />
            <h2>Editar tarifa</h2>
          </div>
          <div className="flex gap-4">
            <Button variant="outline-error" onClick={() => goBack({ fallback: `/fee-templates/${id}` })}>
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="update_fee_templates"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={updateFeeTemplateIsPending}
            >
              {updateFeeTemplateIsPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Confirmar'}
            </Button>
          </div>
        </div>
        {(isLoading || isUserPermissionsPending) ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
            <p className="text-lg font-bold">Detalhes da Tarifa</p>
            <TextField
              label="Nome"
              data-testid="display_name_input"
              errorMessage={errors?.display_name?.message || undefined}
              defaultValue={initialFeeTemplate?.display_name}
              {...register('display_name')}
              disabled={updateFeeTemplateIsPending}
              markAsRequired
            />
            <Checkbox
              label="Tarifa padrão (adicionada em todas condições comerciais para esse tipo de cliente)"
              labelClassName="italic text-xs"
              defaultChecked={initialFeeTemplate?.apply_by_default}
              name="apply_by_default"
              disabled
            />
            <Checkbox
              label="Não aplicar quando houver tarifa especial"
              labelClassName="italic text-xs"
              defaultChecked={initialFeeTemplate?.not_use_if_has_special_fee}
              name="not_use_if_has_special_fee"
              disabled
            />
            <div className="flex gap-2">
              <TextField
                label="Tipo de tarifa"
                data-testid="kind_selector"
                disabled
                className="w-1/4"
                value={feeTemplateKindStrategy[initialFeeTemplate?.kind as keyof typeof feeTemplateKindStrategy]}
              />
              <TextField
                label="Aplicado em"
                data-testid="applicable_entity_selector"
                disabled
                className="w-1/4"
                value={
                  applicableEntityStrategy[initialFeeTemplate?.applicable_entity as keyof typeof applicableEntityStrategy]
                }
              />
              <TextField
                label="Modalidade de pagamento"
                data-testid="payment_modality_selector"
                disabled
                className="w-1/4"
                value={
                  paymentModalityStrategy[initialFeeTemplate?.payment_modality as keyof typeof paymentModalityStrategy]
                }
              />
              <TextField
                label="Medida"
                data-testid="calculate_as_selector"
                disabled
                className="w-1/4"
                value={calculateAsStrategy[initialFeeTemplate?.calculate_as as keyof typeof calculateAsStrategy]}
              />
              <TextField
                label="Valor padrão"
                data-testid="default_value_input"
                disabled
                className="w-1/4"
                value={
                  initialFeeTemplate?.calculate_as === 'percentage'
                    ? formatPercentage(initialFeeTemplate?.default_value || 0)
                    : formatCurrency(initialFeeTemplate?.default_value || 0)
                }
              />
            </div>
            <TextField
              label="Aplicação"
              data-testid="option_id_selector"
              disabled
              value={initialFeeTemplate?.option?.display_name}
            />

            <TextField
              label="Tipo de estabelecimento"
              data-testid='applicable_establishment_commercial_kinds'
              disabled
              value={
                initialFeeTemplate?.applicable_establishment_commercial_kinds
                  ? applicableEstablishmentCommercialKind[initialFeeTemplate?.applicable_establishment_commercial_kinds]
                  : 'Não informado'
              }
            />

            <Accordion
              head={
                <Checkbox
                  label="Consumo mínimo"
                  checked={!!initialFeeTemplate?.conditional_minimum_consumption}
                  disabled
                  name="mininalConsumption"
                />
              }
              body={
                <TextField
                  label="Consumo mínimo"
                  disabled
                  value={
                    initialFeeTemplate?.conditional_minimum_consumption
                      ? formatCurrency(initialFeeTemplate?.conditional_minimum_consumption)
                      : undefined
                  }
                />
              }
            />
            <Accordion
              head={
                <Checkbox
                  label="Produtos e serviços aplicáveis"
                  checked={!!initialFeeTemplate?.fee_template_applicable_products.length}
                  disabled
                  name="applicableProducts"
                />
              }
              body={
                <TextField
                  label="Produtos e serviços aplicáveis"
                  disabled
                  value={initialFeeTemplate?.fee_template_applicable_products
                    .map((product) => product.applicable.display_name)
                    .join(', ')}
                />
              }
            />
            <Accordion
              name="fee_template_applicable_capture_methods"
              head={
                <Checkbox
                  label="Métodos de capturas aplicáveis"
                  checked={!!initialFeeTemplate?.fee_template_applicable_capture_methods?.length}
                  disabled
                  name="applicableCaptureMethods"
                />
              }
              body={
                <TextField
                  name="fee_template_applicable_capture_methods"
                  label="Métodos de capturas aplicáveis"
                  disabled
                  value={initialFeeTemplate?.fee_template_applicable_capture_methods
                    ?.map(({ capture_method }) => capture_method.display_name)
                    ?.join(', ')}
                />
              }
            />
          </Box>
        )}
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
